import { InformationClassification } from "@covestro/mui-theme";
import styled from "@emotion/styled";
import type {
  GigyaData,
  GigyaPreferences,
  GigyaSubscriptions,
} from "@gigya-ts/rest-api";
import type { GigyaWebSDK } from "@gigya-ts/web-sdk";
import type { GigyaJSOnLoginEvent } from "@gigya-ts/web-sdk/dist/types/gigya-helpers";
import CloseIcon from "@mui/icons-material/Close";
import CreateOutlinedIcon from "@mui/icons-material/CreateOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
// import {ImagioOpticalMaterialDataLabel} from "../dataDisplay/ImagioOpticalMaterialDataLabel";
import LaunchIcon from "@mui/icons-material/Launch";
import LoginOutlinedIcon from "@mui/icons-material/LoginOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import MenuIcon from "@mui/icons-material/Menu";
import SearchIcon from "@mui/icons-material/Search";
import {
  AppBar,
  Box,
  Button,
  Chip,
  Container,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Link as MUILink,
  Divider as MuiDivider,
  Slide,
  Stack,
  Toolbar,
  Typography,
  useScrollTrigger,
  useTheme,
} from "@mui/material";
import InputBase from "@mui/material/InputBase";
import { alpha, styled as muiStyled } from "@mui/material/styles";
import { Auth } from "aws-amplify";
import DemoAccountSymbol from "components/commons/DemoAccountSymbol";
import { WelcomeDialog } from "components/dialog";
import { UnauthenticationDialog } from "components/dialog/UnauthenticationDialog";
import { FullScreenLoader } from "components/loader/FullScreenLoader";
import { useCdcScript } from "hooks/use-cdc-script";
import type { CdcUserAttributes } from "models/cdcUser";
import { OPTIC_SIMULATION_URL } from "pages/commons/constants";
import { useNavigationGuard } from "providers/NavigationGuardProvider";
import {
  SettingsContext,
  type SettingsContextValue,
} from "providers/SettingsProvider";
import { useUserInfoContext } from "providers/UserInfoProvider";
import type React from "react";
import { type ReactNode, useContext, useEffect, useRef, useState } from "react";
// import { Auth } from "aws-amplify";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { AuthService } from "service/auth.service";
import { CdcTokenService } from "service/cdc-token.service";
import { CookieService } from "service/cookies";
import {
  localStorageService,
  STORAGE_KEYS,
  sessionStorageService,
} from "service/storage";
import { getDataFromLocalstorageByKey } from "service/utils/get-data-localstorage";
import { getUserType } from "service/utils/get-user-type";
import { useAppConfig } from "../../config/app-config";
// import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  CustomerProviderName,
  federatedLogin,
  useAuthUser,
} from "../../hooks/authentication";
import {
  removeLoginTrackingFlag,
  useLoginTracking,
} from "../../hooks/useLoginTracking";
import { trackLogout } from "../../service/tracking";
import covestroLogo from "./COV_logo_brandmark_2x.png";
import covestroUserProfileActive from "./icon-profile-active.svg";
import { externalProviderName, internalProviderName } from "./layout.constant";

// Initialize SDK with your API Key and Secret.
declare global {
  interface Window {
    onGigyaServiceReady?: () => void;
    gigya?: GigyaWebSDK<GigyaData, GigyaPreferences, GigyaSubscriptions>;
  }
}
window.onGigyaServiceReady = () => {
  (window as any).gigya?.accounts.addEventHandlers({
    onLogin: onCdcLogin,
    onAfterSubmit: (event: any) => {
      console.log("onAfterSubmit", event);
    },
  });
  // refresh token if expired
  const cdcUser = getDataFromLocalstorageByKey(STORAGE_KEYS.CDC_USER);

  if (cdcUser && CdcTokenService.isTokenExpiringSoon(0)) {
    CdcTokenService.refreshToken("gigya-ready");
  }
};

/**
 * CX-3151 follow-up: Gigya's `onLogin` hook fires after the session actually
 * exists (unlike `onAfterSubmit`, which fires before Gigya has established
 * it and got us the documented 403005 "Unauthorized user" verdict). `onLogin`
 * is also the hook Gigya uses when it restores a session from an existing
 * cookie, not just on a fresh interactive login.
 *
 * This is module scope, not a React component, so it can't reach
 * `setGlobalError` from context directly — it dispatches a `CustomEvent` for
 * `CovestroHeader` to pick up, mirroring the existing `auth:session-expired`
 * / `auth:cdc-login` pattern.
 *
 * `onLogin` can fire before `CovestroHeader` mounts (e.g. a cookie-restored
 * session during initial page load), and a plain `CustomEvent` dispatched
 * before any listener exists is lost forever. `pendingCdcLogin` survives
 * that race so the mount effect can pick it up if the event was missed.
 */
let pendingCdcLogin = false;

function onCdcLogin(_eventObj: GigyaJSOnLoginEvent<GigyaData>) {
  pendingCdcLogin = true;
  window.dispatchEvent(new CustomEvent("auth:cdc-login-requested"));
}

/**
 * Resolves when Gigya has answered — measured at ~200ms, versus the 16-21ms it
 * took the old code to reach `window.location.reload()`. That reload aborted the
 * in-flight request, so the session stayed alive on Gigya's side: the next page
 * load restored it through the SSO iframe and `onLogin` promptly logged the user
 * back in. The SDK invokes the callback on its own failures too, so this cannot
 * wait forever and needs no deadline of its own.
 */
const logoutFromCdc = (): Promise<void> =>
  new Promise((resolve) => {
    if (!window.gigya) {
      resolve();
      return;
    }
    window.gigya.accounts.logout({ callback: () => resolve() });
  });

/**
 * CX-3744: only a user-driven auth failure (a request that 401s during active
 * use) opens the re-login dialog. Background detection — page-load/poll expiry
 * checks and Gigya cookie-restore — dispatches `showDialog:false`, so the state
 * clears silently instead of interrupting a passive visitor. A Gigya
 * login/registration screenset in the DOM also suppresses it (CX-3151): the
 * pre-TFA `getJWT` legitimately answers 403005 while the user is signing in.
 */
const shouldOpenSessionDialog = (event: Event): boolean => {
  const showDialog =
    (event as CustomEvent<{ showDialog?: boolean }>).detail?.showDialog ===
    true;
  if (!showDialog) return false;
  return document.querySelector(".gigya-screen-dialog") === null;
};

export interface CovestroHeaderProps {
  title: ReactNode;
}

const HeaderLable = styled.span({
  fontSize: "var(--body-b2-14-regular-size, 14px)",
  fontWeight: 400,
});

const CovestroLogo = styled.img({
  height: 48,
  "@media (min-width: 900px)": {
    height: 64,
  },
});

const CovestroUserProfileActive = styled.img({
  height: 20,
  color: "#a3a3a3",
  marginRight: "4px",
  marginBottom: "3px",
});

const AppLabel = styled.h1({
  margin: 0,
  padding: 0,
  fontSize: "var(--body-b2-14-regular-size, 14px)",
  fontWeight: 400,
  whiteSpace: "nowrap",
});

const Divider = styled.div({
  width: 1,
  height: 25,
  backgroundColor: "#888",
  marginTop: 23,
  marginLeft: 35,
  marginRight: 15,
});

const languagePage = [
  {
    value: "en",
    label: "English",
  },
  // TODO: Currently comment because it is not ready yet. Follow: https://covestro.atlassian.net/browse/DISA-1454
  // {
  //   value: 'de',
  //   label: 'Deutsch'
  // },
  // {
  //   value: 'zh',
  //   label: '中文'
  // },
];

const LoginButton = muiStyled(Button)(({ theme }) => ({
  color: "#000",
  borderRadius: theme.shape.borderRadius,
  textAlign: "left",
  height: "32px",
  width: "100%",
  backgroundColor: "#fff",
  justifyContent: "left",
  "&:hover": {
    color: "#000",
    backgroundColor: "#D9F1FB",
  },
}));

const RegisterButton = muiStyled(Button)(({ theme }) => ({
  color: "inherit",
  borderRadius: theme.shape.borderRadius,
  width: "84px",
  height: "32px",
  border: "1px solid #fff",
  "&:hover": {
    color: "inherit",
  },
}));

const Search = muiStyled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: theme.shape.borderRadius,
  "&:hover": {
    backgroundColor: alpha(theme.palette.common.white, 0.2),
    borderRadius: "4px",
  },
  marginLeft: 0,
  width: "100%",
  [theme.breakpoints.up("sm")]: {
    marginLeft: theme.spacing(1),
    width: "auto",
  },
}));

const StyledInputBase = muiStyled(InputBase)(({ theme }) => ({
  color: "inherit",
  width: "100%",
  "& .MuiInputBase-input": {
    paddingLeft: `calc(1em + ${theme.spacing(2)})`,
    transition: theme.transitions.create("width"),
    [theme.breakpoints.up("sm")]: {
      width: "0",
      "&:focus": {
        width: "10em",
      },
      "&:hover": {
        cursor: "pointer",
      },
    },
  },
}));

const SearchIconWrapper = muiStyled("div")(({ theme }) => ({
  padding: theme.spacing(0, 0.5),
  height: "100%",
  position: "absolute",
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

export const HideOnScroll = ({ children }: React.PropsWithChildren<any>) => {
  const trigger = useScrollTrigger();
  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
};

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: pre-existing top-level header component; the CX-3744 change reduces its branching (extracted shouldOpenSessionDialog). A full decomposition is out of scope for this fix.
export const CovestroHeader = ({ title }: CovestroHeaderProps) => {
  const { settings, setSettings } =
    useContext<SettingsContextValue>(SettingsContext);
  const [appConfig] = useAppConfig();
  const { user, initializing } = useAuthUser();
  const [cdcUser, setCdcUser] = useState<CdcUserAttributes | undefined>(() =>
    CookieService.getToken() && !CookieService.isTokenExpiringSoon(0)
      ? getDataFromLocalstorageByKey(STORAGE_KEYS.CDC_USER)
      : undefined,
  );
  const { userInfo, updateFullAccessNotified } = useUserInfoContext();
  const [showWelcomeDialog, setShowWelcomeDialog] = useState(false);
  const [showSessionExpired, setShowSessionExpired] = useState(false);
  const welcomeDialogDismissed = useRef(false);
  const {
    isLoading: cdcScriptLoading,
    isShowScreenSetLoading,
    showScreenSet,
    setGlobalError,
  } = useCdcScript();
  /* CX-2671: JWT can expire while the tab stays open, or the tab can be
  reopened after expiry. Poll cookie expiry so the username disappears and
  stale localStorage is cleared without waiting for the next API call to 401. */
  const sessionExpiredDispatched = useRef(false);

  useEffect(() => {
    if (
      !welcomeDialogDismissed.current &&
      userInfo?.accessLevel === "FULL" &&
      userInfo.fullAccessNotified === false
    ) {
      setShowWelcomeDialog(true);
    }
  }, [userInfo]);

  useEffect(() => {
    const onSessionExpired = (event: Event) => {
      // Cleanup always runs so the UI drops to the logged-out state promptly
      // (CX-2671), whether or not we also prompt for re-login.
      localStorageService.removeItem(STORAGE_KEYS.CDC_USER);
      // CX-3151: the reload gate is what lets the *next* login take effect.
      // Leaving it set here is what turned an expired session into a permanent
      // lockout — re-logins wrote a valid token that the UI never reflected.
      sessionStorageService.removeItem(STORAGE_KEYS.RELOADED);
      CookieService.removeToken();
      setCdcUser(undefined);
      if (shouldOpenSessionDialog(event)) setShowSessionExpired(true);
    };
    const onCdcLoggedIn = () => {
      setCdcUser(getDataFromLocalstorageByKey(STORAGE_KEYS.CDC_USER));
      setShowSessionExpired(false);
      sessionExpiredDispatched.current = false;
    };
    // CX-3151 follow-up: the actual JWT fetch, moved here from Gigya's
    // `onAfterSubmit` (which fired too early, before the session existed).
    // `onCdcLogin` (module scope, outside React) dispatches this event so it
    // can reach `setGlobalError` from context.
    const onCdcLoginRequested = () => {
      pendingCdcLogin = false;
      CdcTokenService.getCdcJwtAndReload(setGlobalError);
    };
    window.addEventListener("auth:session-expired", onSessionExpired);
    window.addEventListener("auth:cdc-login", onCdcLoggedIn);
    window.addEventListener("auth:cdc-login-requested", onCdcLoginRequested);
    // `onLogin` can fire (e.g. from a restored session cookie) before this
    // effect runs and registers the listener above — in that window the
    // dispatched event is lost. Catch that case via the module-level flag so
    // a missed event still triggers exactly one fetch.
    if (pendingCdcLogin) {
      onCdcLoginRequested();
    }
    return () => {
      window.removeEventListener("auth:session-expired", onSessionExpired);
      window.removeEventListener("auth:cdc-login", onCdcLoggedIn);
      window.removeEventListener(
        "auth:cdc-login-requested",
        onCdcLoginRequested,
      );
    };
  }, [setGlobalError]);

  useEffect(() => {
    const checkExpiry = async () => {
      if (sessionExpiredDispatched.current) return;
      if (!CookieService.getToken() || !CookieService.isTokenExpiringSoon(0))
        return;
      const newToken = await CdcTokenService.refreshToken("expiry-watcher");
      if (!newToken && !sessionExpiredDispatched.current) {
        sessionExpiredDispatched.current = true;
        // Background watcher: clear the logged-out state without popping the
        // dialog on a passive visitor (CX-3744).
        window.dispatchEvent(
          new CustomEvent("auth:session-expired", {
            detail: { showDialog: false },
          }),
        );
      }
    };
    checkExpiry();
    const intervalId = window.setInterval(checkExpiry, 30_000);
    window.addEventListener("focus", checkExpiry);
    document.addEventListener("visibilitychange", checkExpiry);
    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", checkExpiry);
      document.removeEventListener("visibilitychange", checkExpiry);
    };
  }, []);

  // const [cdcUserToken] = useState<string | undefined>(CookieService.getToken());
  const userType = user ? getUserType(user) : "";
  const isAuthed = !!(user?.email || cdcUser?.email);
  useLoginTracking(user, cdcUser);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { requestNavigation } = useNavigationGuard();

  const [anchorElMenuLink, setAnchorElMenuLink] = useState<null | HTMLElement>(
    null,
  );
  const openMenuLink = Boolean(anchorElMenuLink);
  const handleClickMenuLink = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElMenuLink(event.currentTarget);
  };
  const handleCloseMenuLink = () => {
    setAnchorElMenuLink(null);
  };

  const [anchorElLang, setAnchorElLang] = useState<null | HTMLElement>(null);
  const openLang = Boolean(anchorElLang);
  const [currentLang, setCurrentLang] = useState<string>(settings.language);

  const handleClickLang = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElLang(event.currentTarget);
  };
  const handleChangeLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
    const langValue = languagePage.filter((l) => l.value === lang).pop();
    setCurrentLang(langValue?.value.toLocaleUpperCase() ?? "EN");
    setSettings({
      ...settings,
      language: langValue ? langValue.value.toLocaleUpperCase() : "EN",
    });
    setAnchorElLang(null);
  };
  const handleCloseLang = () => {
    setAnchorElLang(null);
  };

  const [anchorElAccount, setAnchorElAccount] = useState<null | HTMLElement>(
    null,
  );
  const openAccount = Boolean(anchorElAccount);
  const handleClickAccount = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElAccount(event.currentTarget);
  };
  const handleCloseAccount = () => {
    setAnchorElAccount(null);
  };

  const handleLogout = () => {
    setAnchorElAccount(null);
    requestNavigation(() => {
      void performLogout();
    });
  };

  const performLogout = async () => {
    // CookieService holds the CDC JWT; Cognito/Azure-SSO users have no cookie,
    // so fall back to the Amplify access token before we clear the session.
    // Tracking is best-effort — never block the logout flow if the token
    // fetch throws (Amplify uninitialized, expired session, network error).
    let bearerToken: string | undefined;
    try {
      bearerToken =
        CookieService.getToken() ?? (await AuthService.getAccessToken());
    } catch {
      bearerToken = undefined;
    }
    trackLogout(bearerToken);
    removeLoginTrackingFlag();
    if (cdcUser) {
      // Local state goes first so the UI reflects the logout immediately; only
      // the reload waits for Gigya.
      localStorageService.removeItem(STORAGE_KEYS.CDC_USER);
      sessionStorageService.removeItem(STORAGE_KEYS.RELOADED);
      setCdcUser(undefined);
      navigate("/");
      await logoutFromCdc();
    }
    CookieService.removeToken();
    try {
      // Awaited for the same reason as `logoutFromCdc`: the reload below cuts
      // short anything still in flight.
      await Auth.signOut({ global: false });
    } catch {
      // Local state is already cleared, so a failed sign-out must not stop the
      // reload that the rest of the app depends on to drop its cached user.
    }
    if (cdcUser) {
      window.location.reload();
    }
  };

  const [anchorElLogin, setAnchorElLogin] = useState<null | HTMLElement>(null);
  const openLogin = Boolean(anchorElLogin);
  const handleClickLogin = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElLogin(event.currentTarget);
  };
  const handleCloseLogin = () => {
    setAnchorElLogin(null);
  };

  const renderUserType = (value: string) => {
    let classificationStr: "CONFIDENTIAL" | "INTERNAL" | undefined;
    if (value === internalProviderName && !settings.externalViewVisible) {
      classificationStr = "CONFIDENTIAL";
    } else if (value === internalProviderName && settings.externalViewVisible) {
      classificationStr = "INTERNAL";
    }
    if (classificationStr) {
      return (
        <>
          <Divider />
          <Box px={2} pt={2.8}>
            <InformationClassification classification={classificationStr} />
          </Box>
        </>
      );
    }
  };

  const theme = useTheme();
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  // const handleChangeLanguage = (value: string) => {
  //   if (!value || value.length === 0) return;
  //   alert('Your language is not support now');
  // };

  const onClickOpenPage = () => {
    window.open(OPTIC_SIMULATION_URL, "_blank", "noopener,noreferrer");
    setAnchorElMenuLink(null);
  };

  const onRegister = (_e: React.MouseEvent<HTMLButtonElement>) => {
    // e.stopPropagation();
    // window.open('https://covestro.onelogin.com/self-registration2?name=SolutionCenter');

    if (cdcScriptLoading || isShowScreenSetLoading) {
      console.warn(
        "CDC script still loading or screen set loading. Please try again in a moment.",
      );
      return;
    }

    // CX-3151: the register screenset can end on the login screen, so it needs
    // the same reload-gate reset `loginCDC` does — otherwise the resulting
    // login is written to storage but never reflected in the UI.
    sessionStorageService.removeItem(STORAGE_KEYS.RELOADED);

    // CX-3151 follow-up: the JWT fetch used to be acquired here from
    // `onAfterSubmit` when the registration screenset ended on the login
    // screen, but that hook fires before Gigya's session exists. It is now
    // triggered from Gigya's `onLogin` handler instead (see `onCdcLogin`).
    showScreenSet({
      screenSet: "Default-RegistrationLogin",
      startScreen: "cov_registration_imagio_cf",
    });
    setAnchorElLogin(null);
  };

  const loginCDC = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    sessionStorageService.removeItem(STORAGE_KEYS.RELOADED);

    if (cdcScriptLoading || isShowScreenSetLoading) {
      console.warn(
        "CDC script still loading or screen set loading. Please try again in a moment.",
      );
      return;
    }

    // CX-3151 follow-up: the JWT fetch used to be acquired here from
    // `onAfterSubmit`, but that hook fires before Gigya's session exists. It
    // is now triggered from Gigya's `onLogin` handler instead (see
    // `onCdcLogin`).
    showScreenSet({
      screenSet: "Default-RegistrationLogin",
      startScreen: "cov_login",
    });
    setAnchorElLogin(null);
  };

  const loginForCovestroEmployees = (
    e: React.MouseEvent<HTMLButtonElement>,
  ) => {
    e.stopPropagation();
    federatedLogin(CustomerProviderName.CovestroAzureSSO);
    setAnchorElLogin(null);
    localStorageService.removeItem(STORAGE_KEYS.CDC_USER);
    sessionStorageService.removeItem(STORAGE_KEYS.RELOADED);
    CookieService.removeToken();
    setCdcUser(undefined);
  };

  const handleOpenHelp = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    requestNavigation(() => navigate(`/help-center`));
  };

  const handleRouteOnUserType = () => {
    requestNavigation(() => {
      if (userType === externalProviderName || cdcUser) {
        navigate("/profile");
      } else {
        const pathName = window.location.pathname;
        const routeBreak = window.location.href.split(pathName);
        localStorage.setItem(
          "rememberRoute",
          pathName + routeBreak[routeBreak.length - 1],
        );
        navigate("/settings");
      }
    });
  };

  const isInitialized = useRef(false);

  useEffect(() => {
    if (
      !isInitialized.current &&
      cdcUser &&
      cdcUser?.extData?.custom?.language
    ) {
      console.log("init lang: ", cdcUser?.extData?.custom?.language);
      setSettings({ ...settings, language: "EN" });
      setCurrentLang("EN");
      isInitialized.current = true;
    }
  }, [cdcUser, setSettings, settings]);

  useEffect(() => {
    if (currentLang && currentLang !== "") {
      i18n.changeLanguage(currentLang.toLocaleLowerCase());
    }
  }, [currentLang, i18n]);

  return (
    <AppBar
      id="CovestroHeaderTop"
      position="static"
      elevation={2}
      sx={{
        backgroundColor: theme.palette.common.black,
        color: theme.palette.common.white,
        height: { xs: "64px", md: "96px" },
      }}
    >
      {/* zIndex 1500 keeps the loader above sticky nav (z=30) and category
          panels (z=30), matching AxFDownloadDialog's convention. Not part of
          CX-2268 — found while reviewing related dead-code wrappers. */}
      {isShowScreenSetLoading && <FullScreenLoader zIndex={1500} />}
      <WelcomeDialog
        isOpen={showWelcomeDialog}
        onClose={() => {
          welcomeDialogDismissed.current = true;
          setShowWelcomeDialog(false);
          updateFullAccessNotified(true);
        }}
        onClickConfirm={() => {
          welcomeDialogDismissed.current = true;
          setShowWelcomeDialog(false);
          updateFullAccessNotified(true);
        }}
      />
      <UnauthenticationDialog
        isOpen={showSessionExpired}
        onClose={() => setShowSessionExpired(false)}
        reason="session-expired"
      />
      <Toolbar sx={{ height: "100%" }}>
        <Container maxWidth={"xxl"}>
          <Stack
            alignItems="center"
            direction="row"
            pt={2}
            pb={2}
            justifyContent="space-between"
          >
            <Box sx={{ flexGrow: 1, display: "flex" }}>
              <Link
                to="/"
                style={{ color: "inherit", textDecoration: "inherit" }}
                onClick={(e) => {
                  e.preventDefault();
                  requestNavigation(() => navigate("/"));
                }}
              >
                <Stack alignItems="center" direction="row" spacing={2}>
                  <CovestroLogo
                    src={covestroLogo}
                    alt={t("header_covestro_logo")}
                  />
                  {title}
                  <DemoAccountSymbol
                    isShow={userInfo?.accessLevel === "DEMO"}
                  />
                  {appConfig.environment?.showEnvironmentName && (
                    <Chip
                      color="secondary"
                      style={{
                        fontWeight: 500,
                        fontSize: "var(--body-b2-14-regular-size, 14px)",
                      }}
                      label={appConfig.environment.name}
                    />
                  )}
                </Stack>
                <Stack alignItems="center" direction="row" spacing={2}></Stack>
              </Link>
              {renderUserType(userType)}
            </Box>
            <IconButton
              aria-label={t("header_open_menu") || "Open menu"}
              onClick={() => setMobileDrawerOpen(true)}
              sx={{
                color: "white",
                display: { xs: "inline-flex", md: "none" },
                width: 44,
                height: 44,
              }}
              size="large"
            >
              <MenuIcon />
            </IconButton>
            <Box
              sx={{
                display: { xs: "none", md: "flex" },
                alignItems: "center",
              }}
            >
              {appConfig.general_search?.enabled && (
                <Box px={1}>
                  <Search>
                    <SearchIconWrapper>
                      <SearchIcon />
                    </SearchIconWrapper>
                    <StyledInputBase
                      placeholder={t("header_general_search")}
                      inputProps={{ "aria-label": "search" }}
                    />
                  </Search>
                </Box>
              )}
              <Box px={1}>
                {userType === internalProviderName && (
                  <IconButton
                    sx={{ color: "white", opacity: 0.9 }}
                    size="medium"
                    onClick={handleClickMenuLink}
                  >
                    <HeaderLable>{t("header_imagio_open_link")}</HeaderLable>
                    <ExpandMoreIcon fontSize="medium" />
                  </IconButton>
                )}
                <Menu
                  open={openMenuLink}
                  anchorEl={anchorElMenuLink}
                  onClose={handleCloseMenuLink}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "center",
                  }}
                >
                  <MenuItem key={"openImagio"} onClick={onClickOpenPage}>
                    <MUILink
                      component="span"
                      underline="hover"
                      sx={{ cursor: "pointer" }}
                    >
                      <Typography component={"span"} fontSize="inherit">
                        <HeaderLable>
                          {t("header_imagio_open_link_item")}
                        </HeaderLable>
                        <LaunchIcon
                          sx={{
                            fontSize: "12pt",
                            lineHeight: "1em",
                            verticalAlign: "text-top",
                            ml: 0.5,
                          }}
                        />
                      </Typography>
                    </MUILink>
                  </MenuItem>
                </Menu>
              </Box>
              <Box>
                <IconButton
                  sx={{ color: "white", opacity: 1 }}
                  size="medium"
                  onClick={handleClickLang}
                >
                  <AppLabel>
                    {currentLang === "ZH" ? "中文" : currentLang}
                  </AppLabel>
                </IconButton>
                <Menu
                  open={openLang}
                  anchorEl={anchorElLang}
                  onClose={handleCloseLang}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "center",
                  }}
                  MenuListProps={{
                    sx: { minWidth: 20 },
                  }}
                >
                  {languagePage.map((lang) => (
                    <MenuItem
                      key={lang.value}
                      onClick={() => handleChangeLanguage(lang.value)}
                    >
                      <Stack
                        direction={"row"}
                        justifyContent={"space-between"}
                        alignItems={"center"}
                        width={"100%"}
                      >
                        <Typography
                          style={{
                            fontWeight: 400,
                            fontSize: "var(--body-b2-14-regular-size, 14px)",
                          }}
                        >
                          {lang.label}
                        </Typography>
                      </Stack>
                    </MenuItem>
                  ))}
                </Menu>
              </Box>
              {appConfig.help_center?.enabled && (
                <Box>
                  <IconButton
                    sx={{ color: "white", opacity: 0.9 }}
                    size="medium"
                    onClick={handleOpenHelp}
                  >
                    <HelpOutlineIcon sx={{ height: "20px", width: "20px" }} />
                  </IconButton>
                </Box>
              )}
              {initializing ? null : isAuthed ? (
                <Box>
                  <IconButton
                    onClick={handleClickAccount}
                    sx={{ color: "#fff", opacity: 0.9 }}
                    size="medium"
                  >
                    <CovestroUserProfileActive
                      src={covestroUserProfileActive}
                      alt={t("header_covestro_profile_active")}
                    />
                    <AppLabel>
                      {cdcUser?.email
                        ? `${cdcUser?.firstName} ${cdcUser?.lastName}`
                        : `${user?.given_name} ${user?.family_name}`}
                    </AppLabel>
                    <ExpandMoreIcon fontSize="medium" />
                  </IconButton>
                  <Menu
                    open={openAccount}
                    anchorEl={anchorElAccount}
                    onClose={handleCloseAccount}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "center",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "center",
                    }}
                    MenuListProps={{
                      sx: { minWidth: 211 },
                    }}
                  >
                    <MenuItem
                      key="AccountDetail"
                      onClick={() => setAnchorElAccount(null)}
                    >
                      <MUILink
                        component="button"
                        onClick={handleRouteOnUserType}
                        style={{ textDecoration: "none", color: "inherit" }}
                      >
                        <Stack
                          direction={"row"}
                          alignItems={"center"}
                          width={"100%"}
                        >
                          <CreateOutlinedIcon fontSize="small" />
                          <Typography
                            style={{
                              fontWeight: 400,
                              fontSize: "var(--body-b2-14-regular-size, 14px)",
                              marginLeft: "6px",
                            }}
                          >
                            {userType === externalProviderName || cdcUser
                              ? t("header_user_profile")
                              : t("header_user_preferences")}
                          </Typography>
                        </Stack>
                      </MUILink>
                    </MenuItem>
                    <MenuItem key="LogoutAction" onClick={handleLogout}>
                      <Link
                        to="/"
                        style={{ textDecoration: "none", color: "inherit" }}
                        onClick={(e) => e.preventDefault()}
                      >
                        <Stack
                          direction={"row"}
                          alignItems={"center"}
                          width={"100%"}
                        >
                          <LogoutOutlinedIcon fontSize="small" />
                          <Typography
                            style={{
                              fontWeight: 400,
                              fontSize: "var(--body-b2-14-regular-size, 14px)",
                              marginLeft: "6px",
                            }}
                          >
                            {t("header_logout")}
                          </Typography>
                        </Stack>
                      </Link>
                    </MenuItem>
                  </Menu>
                </Box>
              ) : (
                <Box px={1}>
                  <RegisterButton size="small" onClick={(e) => onRegister(e)}>
                    {t("header_register")}
                  </RegisterButton>
                  <Button
                    onClick={handleClickLogin}
                    variant="contained"
                    sx={{ marginLeft: "8px", height: "32px", width: "84px" }}
                    size="small"
                    endIcon={<ExpandMoreIcon />}
                  >
                    {t("header_login")}
                  </Button>
                  <Menu
                    open={openLogin}
                    anchorEl={anchorElLogin}
                    onClose={handleCloseLogin}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "center",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "center",
                    }}
                    MenuListProps={{
                      sx: { minWidth: 211 },
                    }}
                  >
                    <MenuItem
                      key="loginCustomer"
                      sx={{ padding: 0, textAlign: "left" }}
                    >
                      <LoginButton
                        onClick={(e) => loginCDC(e)}
                        variant="contained"
                        size="medium"
                      >
                        {t("header_login_customer")}
                      </LoginButton>
                    </MenuItem>
                    <MenuItem
                      key="loginCovestroEmployee"
                      sx={{ padding: 0, textAlign: "left" }}
                    >
                      <LoginButton
                        onClick={(e) => loginForCovestroEmployees(e)}
                        variant="contained"
                        size="medium"
                      >
                        {t("header_login_covestro_employee")}
                      </LoginButton>
                    </MenuItem>
                    {/* <MenuItem key='loginCDC' sx={{padding: 0, textAlign: "left"}}>
                    <LoginButton disabled onClick={(e) => loginCDC(e)} variant="contained" size="medium">
                      <b>CDC Login (test-demo)</b>
                    </LoginButton>
                  </MenuItem> */}
                  </Menu>
                </Box>
              )}
            </Box>
          </Stack>
        </Container>
      </Toolbar>
      <Drawer
        anchor="right"
        open={mobileDrawerOpen}
        onClose={() => setMobileDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: "85vw", sm: "360px" },
            maxWidth: "360px",
            backgroundColor: theme.palette.common.black,
            color: theme.palette.common.white,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1.5,
            borderBottom: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          <CovestroLogo
            src={covestroLogo}
            alt={t("header_covestro_logo")}
            style={{ height: 40 }}
          />
          <IconButton
            onClick={() => setMobileDrawerOpen(false)}
            sx={{
              color: "white",
              width: 44,
              height: 44,
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.08)",
              },
            }}
            aria-label={t("header_close_menu") || "Close menu"}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <List sx={{ px: 2, pt: 2 }}>
          {!initializing && !isAuthed && (
            <>
              <ListItem disablePadding sx={{ mb: 1.5 }}>
                <ListItemButton
                  onClick={(e) => {
                    setMobileDrawerOpen(false);
                    loginCDC(
                      e as unknown as React.MouseEvent<HTMLButtonElement>,
                    );
                  }}
                  sx={{
                    borderRadius: 1,
                    minHeight: 52,
                    px: 2,
                    backgroundColor: theme.palette.primary.main,
                    boxShadow: "0 2px 8px rgba(0, 159, 228, 0.35)",
                    "&:hover": {
                      backgroundColor: theme.palette.primary.dark,
                    },
                    "&:active": {
                      transform: "translateY(1px)",
                    },
                  }}
                >
                  <ListItemIcon sx={{ color: "white", minWidth: 32 }}>
                    <LoginOutlinedIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("header_login_customer")}
                    primaryTypographyProps={{
                      fontSize: "var(--body-b1-16-regular-size, 16px)",
                      fontWeight: 700,
                    }}
                  />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding sx={{ mb: 1.5 }}>
                <ListItemButton
                  onClick={() => {
                    setMobileDrawerOpen(false);
                    onRegister({} as React.MouseEvent<HTMLButtonElement>);
                  }}
                  sx={{
                    borderRadius: 1,
                    minHeight: 48,
                    px: 2,
                    border: "1px solid rgba(255,255,255,0.5)",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,0.08)",
                      borderColor: "white",
                    },
                  }}
                >
                  <ListItemText
                    primary={t("header_register")}
                    primaryTypographyProps={{
                      fontSize: "var(--body-b1-16-regular-size, 16px)",
                      fontWeight: 500,
                      textAlign: "center",
                    }}
                  />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding sx={{ mb: 1 }}>
                <ListItemButton
                  onClick={(e) => {
                    setMobileDrawerOpen(false);
                    loginForCovestroEmployees(
                      e as unknown as React.MouseEvent<HTMLButtonElement>,
                    );
                  }}
                  sx={{
                    borderRadius: 1,
                    minHeight: 44,
                    px: 2,
                    color: "rgba(255,255,255,0.7)",
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,0.06)",
                      color: "white",
                    },
                  }}
                >
                  <ListItemIcon sx={{ color: "inherit", minWidth: 32 }}>
                    <LoginOutlinedIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("header_login_covestro_employee")}
                    primaryTypographyProps={{
                      fontSize: "var(--body-b2-14-regular-size, 14px)",
                    }}
                  />
                </ListItemButton>
              </ListItem>
              <MuiDivider
                sx={{ my: 2, borderColor: "rgba(255,255,255,0.12)" }}
              />
            </>
          )}
          {!initializing && isAuthed && (
            <>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    setMobileDrawerOpen(false);
                    handleRouteOnUserType();
                  }}
                  sx={{ borderRadius: 1, minHeight: 48 }}
                >
                  <ListItemIcon sx={{ color: "white", minWidth: 36 }}>
                    <CreateOutlinedIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      userType === externalProviderName || cdcUser
                        ? t("header_user_profile")
                        : t("header_user_preferences")
                    }
                    secondary={
                      cdcUser?.email
                        ? `${cdcUser?.firstName} ${cdcUser?.lastName}`
                        : `${user?.given_name} ${user?.family_name}`
                    }
                    secondaryTypographyProps={{
                      fontSize: "var(--body-b3-12-regular-size, 12px)",
                      color: "rgba(255,255,255,0.6)",
                    }}
                  />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    setMobileDrawerOpen(false);
                    handleLogout();
                  }}
                  sx={{ borderRadius: 1, minHeight: 48 }}
                >
                  <ListItemIcon sx={{ color: "white", minWidth: 36 }}>
                    <LogoutOutlinedIcon />
                  </ListItemIcon>
                  <ListItemText primary={t("header_logout")} />
                </ListItemButton>
              </ListItem>
              <MuiDivider
                sx={{ my: 1, borderColor: "rgba(255,255,255,0.2)" }}
              />
            </>
          )}
          {appConfig.help_center?.enabled && (
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => {
                  setMobileDrawerOpen(false);
                  requestNavigation(() => navigate("/help-center"));
                }}
                sx={{ borderRadius: 1, minHeight: 48 }}
              >
                <ListItemIcon sx={{ color: "white", minWidth: 36 }}>
                  <HelpOutlineIcon />
                </ListItemIcon>
                <ListItemText primary={t("header_help") || "Help"} />
              </ListItemButton>
            </ListItem>
          )}
          {languagePage.length > 1 &&
            languagePage.map((lang) => (
              <ListItem disablePadding key={`mobile-lang-${lang.value}`}>
                <ListItemButton
                  onClick={() => {
                    setMobileDrawerOpen(false);
                    handleChangeLanguage(lang.value);
                  }}
                  selected={
                    currentLang.toLowerCase() === lang.value.toLowerCase()
                  }
                  sx={{ borderRadius: 1, minHeight: 48 }}
                >
                  <ListItemText primary={lang.label} />
                </ListItemButton>
              </ListItem>
            ))}
        </List>
      </Drawer>
    </AppBar>
  );
};
